<template>
    <div class="forum-wrapper">
        <div class="forum-layout">
            <!-- Main Column -->
            <div class="main-column">
                <!-- Forum Header -->
                <header class="forum-header">
                    <h1 class="typo-headline-4">Foro de la Comunidad</h1>
                    
                    <!-- Link to forum rules -->
                    <div class="forum-rules-link">
                        <router-link to="/app/forum/rules" class="rules-link typo-button">
                            <i class="material-icons">gavel</i>
                            Normas del Foro
                        </router-link>
                    </div>
                </header>

                <!-- View Selector -->
                <div class="view-selector-container">
                    <div class="view-selector">
                        <div 
                            @click="activeView = 'recent'" 
                            class="view-tab" 
                            :class="{ 'active': activeView === 'recent' }"
                        >
                            <i class="material-icons">update</i>
                            Contenido reciente
                        </div>
                        <div 
                            @click="activeView = 'valores'" 
                            class="view-tab" 
                            :class="{ 'active': activeView === 'valores' }"
                        >
                            <i class="material-icons">trending_up</i>
                            Foro de Valores
                        </div>
                        <div 
                            @click="activeView = 'index'" 
                            class="view-tab" 
                            :class="{ 'active': activeView === 'index' }"
                        >
                            <i class="material-icons">list</i>
                            Índice de foro
                        </div>
                        <div 
                            @click="activeView = 'search'" 
                            class="view-tab" 
                            :class="{ 'active': activeView === 'search' }"
                        >
                            <i class="material-icons">search</i>
                            Búsqueda de foro
                        </div>
                    </div>
                </div>

                <!-- Main Content -->
                <section class="forum-content">
                    <!-- Recent Content View -->
                    <div v-if="activeView === 'recent'" class="recent-content-view">
                        <full-recent-content />
                    </div>
                    
                    <!-- Valores Content View -->
                    <div v-else-if="activeView === 'valores'" class="valores-content-view">
                        <forum-valores />
                    </div>
                    
                    <!-- Search Content View -->
                    <div v-else-if="activeView === 'search'" class="search-content-view">
                        <forum-search />
                    </div>
                    
                    <!-- Forum Index View -->
                    <div v-else>
                        <!-- Estados de carga/error -->
                        <div v-if="loading" class="loading-message typo-body-2">
                            Cargando contenido del foro...
                        </div>
                        <div v-else-if="error" class="error-message typo-body-2">
                            Error al cargar el contenido del foro.
                        </div>

                        <div v-else>
                            <!-- Dynamic Forum Areas -->
                            <div v-for="(section, index) in forumSections" :key="index" class="forum-section">
                                <forum-area 
                                    :title="section.title" 
                                    :sections="section.sections"
                                    class="forum-area"
                                />
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <!-- Side Column -->
            <div class="side-column">
                <!-- Admin Activity Widget -->
                <admin-activity-widget />

                <!-- User Activity Widget -->
                <user-activity-widget />

                <!-- Trending Topics -->
                <!-- <trending-topics-container /> -->

                <!-- Forum Stats -->
                <forum-stats-container />
            </div>
        </div>
    </div>
</template>

<script>
import forumApi from '@/features/forum/services/forumApi';
import { legacyForumView } from '@/features/forum/services/forumLegacyLocation';
const axios = forumApi;
import ForumArea from './components/ForumArea.vue';
import ForumStatsContainer from '../components/ForumStatsContainer.vue';
import UserActivityWidget from '../components/UserActivityWidget.vue';
import AdminActivityWidget from '../components/AdminActivityWidget.vue';
import FullRecentContent from './components/FullRecentContent.vue';
import ForumValores from './components/ForumValores.vue';
import ForumSearch from './components/ForumSearch.vue';

export default {
    name: 'ForumIndex',
    components: {
        ForumArea,
        ForumStatsContainer,
        UserActivityWidget,
        AdminActivityWidget,
        FullRecentContent,
        ForumValores,
        ForumSearch
    },
    data() {
        return {
            threads: [],
            loading: true,
            error: null,
            mainForumSections: [],
            secondaryForumSections: [],
            activeView: legacyForumView(this.$route.query.view),
            forumSections: []
        };
    },
    watch: {
      activeView(newView) {
        if (legacyForumView(this.$route.query.view) === newView) return;
        // Update the URL query parameter when the tab changes
        this.$router.replace({ query: { ...this.$route.query, view: newView } }).catch(error => {
          if (error.name !== 'NavigationDuplicated') throw error;
        });
      },
      '$route.query.view'(value) {
        this.activeView = legacyForumView(value);
      }
    },
    mounted() {
        this.fetchForumSections();
    },
    methods: {
        fetchForumSections() {
            this.loading = true;
            this.error = null;
            
            axios
                .get('/forum/api/structure')
                .then(response => {
                    if (response.data) {
                        // The data already comes in the hierarchical structure we need
                        const forumData = response.data;
                        
                        // Each top-level forum becomes a section
                        const sections = [];
                        
                        // Process each top-level forum
                        Object.keys(forumData).forEach(tid => {
                            const forum = forumData[tid];
                            
                            // Convert subforums to the format expected by the component
                            const subforums = [];
                            if (forum.children && Object.keys(forum.children).length > 0) {
                                Object.keys(forum.children).forEach(childTid => {
                                    const child = forum.children[childTid];
                                    subforums.push({
                                        id: parseInt(childTid),
                                        title: child.name,
                                        slug: child.slug,
                                        description: '', // No description yet - Can be added from child.description if available
                                        // Map stats from API response
                                        threadCount: child.topicCount || 0, 
                                        postCount: child.postCount || 0,
                                        lastActivityTimestamp: child.lastPostTimestamp || null,
                                        lastActivityAuthor: child.lastPostAuthorName || 'N/A',
                                        lastActivityNodeId: child.lastPostNodeId || null,
                                        // Keep defaults or map from API if needed
                                        icon: 'forum', 
                                        url: child.url,
                                        depth: child.depth,
                                        weight: child.weight || 0
                                    });
                                });
                                
                                // Sort subforums by weight
                                subforums.sort((a, b) => a.weight - b.weight);
                            }
                            
                            // Create a section for this forum
                            sections.push({
                                title: forum.name,
                                sections: subforums,
                                weight: forum.weight || 0
                            });
                        });
                        
                        // Sort sections by weight
                        sections.sort((a, b) => a.weight - b.weight);
                        
                        // Update the component's data
                        this.forumSections = sections;
                    } else {
                        throw new Error('Invalid response format');
                    }
                })
                .catch(err => {
                    console.error('Error fetching forum sections:', err);
                    this.error = 'Error al cargar las secciones del foro';
                    this.forumSections = []; // Clear sections on error
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        goToThread(thread) {
            this.$router.push({ name: 'ForumThread', params: { nid: thread.nid } });
        },
        goToForumSection(section) {
            // Debug log removed
            this.$router.push({ 
                name: 'ForumSection', 
                params: { slug: section.slug } 
            });
        },
        createNewThread() {
            this.$router.push({ name: 'ForumNewThread' });
        },
    },
};
</script>

<style scoped>
/* Contenedor general */
.forum-wrapper {
    background-color: var(--color-background);
    min-height: 100vh;
    padding: 40px 20px;
}

/* Layout de dos columnas */
.forum-layout {
    max-width: 1200px;
    margin: 0 auto;
    display: flex;
    justify-content: center;
    gap: 20px;
}

/* Estilos de la columna principal */
.main-column {
    flex: 3;
    color: var(--color-text-primary);
}

/* Cabecera del foro */
.forum-header {
    margin-bottom: 20px;
    padding: 20px;
    background-color: var(--color-surface);
    border-radius: 15px;
    display: flex;
    flex-direction: column;
    gap: 15px;
    align-items: center;
}

.forum-header h1 {
    margin: 0;
}

/* View Selector */
.view-selector-container {
    margin-bottom: 20px;
    border-radius: 15px;
    overflow: hidden;
}

.view-selector {
    display: flex;
    width: 100%;
    background-color: var(--color-surface);
    border-radius: 8px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.view-tab {
    flex: 1;
    padding: 14px 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    background-color: var(--color-surface);
    color: var(--color-text-secondary);
    cursor: pointer;
    transition: all 0.3s ease;
    font-weight: 500;
    text-align: center;
    border-bottom: 3px solid transparent;
}

.view-tab i {
    font-size: 20px;
}

.view-tab.active {
    color: var(--color-highlight);
    background-color: var(--color-surface);
    border-bottom: 3px solid var(--color-highlight);
    font-weight: 600;
}

.view-tab:hover:not(.active) {
    background-color: var(--color-active-surface);
    color: var(--color-text-primary);
}

.forum-rules-link {
    margin-top: 10px;
}

.rules-link {
    display: flex;
    align-items: center;
    gap: 5px;
    color: var(--color-highlight);
    text-decoration: none;
    padding: 8px 16px;
    border-radius: 4px;
    transition: background-color 0.3s ease;
}

.rules-link:hover {
    background-color: var(--color-active-surface);
}

.rules-link i {
    font-size: 18px;
}

/* Contenido principal */
.forum-content {
    margin-bottom: 20px;
}

/* Recent Content, Valores and Search View */
.recent-content-view,
.valores-content-view,
.search-content-view {
    /* El contenedor para la vista de contenido reciente, foro de valores y búsqueda */
}

.search-content-view .iframe-container {
    background-color: var(--color-surface);
    border-radius: 15px;
    overflow: hidden;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.search-content-view iframe {
    border: none;
    width: 100%;
    min-height: 600px;
}

/* Áreas del foro */
.forum-section {
    margin-bottom: 20px;
}

.forum-area {
    background-color: var(--color-surface);
    border-radius: 15px;
    overflow: hidden;
}

/* Columna lateral */
.side-column {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 20px;
}

.user-stats,
.forum-stats {
    background-color: var(--color-surface);
    padding: 20px;
    border-radius: 15px;
    color: var(--color-text-primary);
}

.user-stats h3,
.forum-stats h3 {
    margin-top: 0;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 1px solid var(--color-outline);
}

/* Estadísticas de usuario */
.stats-content {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.stat-item {
    display: flex;
    justify-content: space-between;
    padding: 5px 0;
}

.create-thread-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 5px;
    background-color: var(--color-highlight);
    color: var(--color-on-highlight, white);
    border: none;
    padding: 10px;
    border-radius: 4px;
    margin-top: 15px;
    cursor: pointer;
    transition: background-color 0.3s ease;
    width: 100%;
}

.create-thread-btn:hover {
    background-color: var(--color-highlight-dark, #1976d2);
}

.login-prompt {
    text-align: center;
}

.login-btn {
    background-color: var(--color-highlight);
    color: var(--color-on-highlight, white);
    border: none;
    padding: 10px 20px;
    border-radius: 4px;
    margin-top: 10px;
    cursor: pointer;
    transition: background-color 0.3s ease;
}

.login-btn:hover {
    background-color: var(--color-highlight-dark, #1976d2);
}

/* Mensajes de carga y error */
.loading-message,
.error-message {
    padding: 20px;
    background-color: var(--color-surface);
    border-radius: 8px;
    text-align: center;
    margin: 20px 0;
}

.error-message {
    border-left: 4px solid #f44336;
}

/* Responsividad */
@media (max-width: 768px) {
    .forum-layout {
        flex-direction: column;
    }
    
    .main-column,
    .side-column {
        flex: 1;
        width: 100%;
    }
    
    .side-column {
        margin-top: 20px;
    }

    .view-tab {
        padding: 12px 8px;
        font-size: 14px;
    }
    
    .view-tab i {
        font-size: 18px;
    }

}
</style>
